import { ApiError, ApiErrorCode } from "../models/api-error.model";
import { ApiPage, emptyApiPage } from "../models/api-page.model";
import { CreateOffice, OfficeSignUp, OfficeSignUpV2 } from "../models/create-office.model";
import { Document } from "../models/document.model";
import { DsFile, RequestFileTypes } from "../models/ds-file.model";
import { AccountReceivablesTotals, Finance, FinanceRequest, Payee, TopOverdueAccounts } from "../models/finance.model";
import { InvitationResponse } from "../models/invitation-response.model";
import { Office, OfficeState } from "../models/office.model";
import { PatientInviteObject } from "../models/patient.model";
import { PriceDetails } from "../models/price-details.model";
import { Receipt } from "../models/receipt.model";
import { RequestCounts } from "../models/request-counts.model";
import { RequestFile } from "../models/request-file.model";
import { RequestMessage } from "../models/request-message.model";
import { RequestPatientUpdate } from "../models/request-patient-update.model";
import { ArchiveState, Request, RequestPaymentMethod, RequestState } from "../models/request.model";
import { Service } from "../models/service.model";
import { Timeline } from "../models/timeline.model";
import { UpdateFee } from "../models/update-fee.model";
import { UpdateOfficeBilling } from "../models/update-office-billing.model";
import { UpdateOffice } from "../models/update-office.model";
import { UpdateRequest } from "../models/update-request.model";
import { UserAnnualPlan } from "../models/user-annual-plan.model";
import { UserInfo } from "../models/user-info.model";
import { UserUpdate } from "../models/user-update.model";
import { User } from "../models/user.model";
import { ApiPatchOp, apiService } from "./api.service";
import { AuthPermission } from "./auth.service";
import { UserType } from "./user.service";

export class OfficeService {
  private static BASE_PATH = "/api/v1/offices";
  private static BASE_PATH_V2 = "/api/v2/offices";
  private static BASE_PATH_USER = "/api/v1";
  static async searchUserAnnualPlans(
    officeId: string,
    term: string,
    page = 0,
    size = 20,
    sort = "user.lastName,desc"
  ): Promise<ApiPage<UserAnnualPlan>> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/user-annual-plans/search?page=${page}&size=${size}&sort=${sort}`, {
      term: term,
    });
    if (!response.success) return emptyApiPage<UserAnnualPlan>();

    return response.data as ApiPage<UserAnnualPlan>;
  }

  static async searchOffices(term: string, page = 0, size = 20, sort = "name,asc", officeStates?: OfficeState[]): Promise<ApiPage<Office>> {
    const response = await apiService.post(`${this.BASE_PATH}/search?page=${page}&size=${size}&sort=${sort}`, {
      term: term,
      officeStates,
    });
    if (!response.success) return emptyApiPage<Office>();

    return response.data as ApiPage<Office>;
  }

  static async getAllOffices(page = 0, size = 10, term = ""): Promise<ApiPage<Office>> {
    const payload: { term?: string; officeStates: OfficeState[] | string[] } = {
      officeStates: ["draft", "published"],
    };

    const trimmedTerm = term.trim();
    if (trimmedTerm.length > 0) {
      payload.term = trimmedTerm;
    }

    const response = await apiService.post(`${this.BASE_PATH_V2}/search/offices?page=${page}&size=${size}`, payload);
    if (!response.success) return emptyApiPage<Office>();

    return response.data as ApiPage<Office>;
  }

  static async lookupOffice(term: string, page = 0, size = 20, sort = "name,asc"): Promise<ApiPage<Office>> {
    const response = await apiService.post(`${this.BASE_PATH}/lookup?page=${page}&size=${size}&sort=${sort}`, {
      term: term,
    });
    if (!response.success) return emptyApiPage<Office>();

    return response.data as ApiPage<Office>;
  }

  static async getOffice(officeId: string): Promise<Office | null> {
    const response = await apiService.get(`${this.BASE_PATH_V2}/${officeId}`);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async getFileData(officeId: string, userID?: string): Promise<Office | null> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/user/${userID}/sign-file`);
    if (!response.success) return null;

    return response.data as Office;
  }
  static async deleteUserFile(userID: string, type: string): Promise<boolean> {
    const response = await apiService.delete(`${this.BASE_PATH_USER}/users/${userID}/${type}`);
    if (!response.success) return false;
    return true;
  }

  static async deleteOfficeFile(officeId: string, type: string): Promise<boolean> {
    const response = await apiService.delete(`${this.BASE_PATH}/${officeId}/${type}`);
    if (!response.success) return false;
    return true;
  }

  static async getUserHeadLogo(userID?: string): Promise<Office | null> {
    const response = await apiService.get(`${this.BASE_PATH_USER}/users/${userID}/letter-header-logo`);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async getOfficeHeadLogo(officeId?: string): Promise<Office | null> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/letter-header-logo`);
    if (!response.success) return null;

    return response.data as Office;
  }

  // Returns saved office with ID included, or null on failure
  static async addOffice(office: CreateOffice): Promise<Office | null> {
    const response = await apiService.post(`${this.BASE_PATH}`, office);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async updateOffice(office: UpdateOffice): Promise<Office | null> {
    const response = await apiService.put(`${this.BASE_PATH}/${office.officeId}`, office);
    if (!response.success) return response.data;

    return response.data as Office;
  }

  static async updateOfficeBilling(office: UpdateOfficeBilling): Promise<Office | null> {
    const response = await apiService.put(`${this.BASE_PATH}/${office.officeId}/billing`, office);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async deleteOffice(officeId: string): Promise<boolean> {
    const response = await apiService.delete(`${this.BASE_PATH}/${officeId}`);
    if (!response.success) return false;

    return true;
  }

  static async addOfficeService(officeId: string, service: Service): Promise<Service | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/services`, service);
    if (!response.success) return null;

    return response.data as Service;
  }

  static async addOfficeServicesV2(officeId: string, services: { officeId: string; name: string; price: number }[]): Promise<Service[] | null> {
    // TODO: Backend has duplicate "offices" in path - fix later
    const response = await apiService.post(`${this.BASE_PATH_V2}/${officeId}/add-services`, { services });
    if (!response.success) return null;

    return response.data as Service[];
  }

  static async saveMpinSignature(
    officeId: string,
    userId: string,
    mpin: string,
    signatureFileId: string
  ): Promise<{ success: boolean; errorMessage?: string }> {
    const response = await apiService.post(
      `${this.BASE_PATH_V2}/${officeId}/users/${userId}/mpin-signature?mpin=${encodeURIComponent(mpin)}&signatureFileId=${encodeURIComponent(
        signatureFileId
      )}`,
      {}
    );
    if (!response.success) {
      return { success: false, errorMessage: response.data?.message || "Failed to save MPN and signature" };
    }
    return { success: true };
  }

  static async updateOfficeService(officeId: string, service: Service): Promise<Service | null> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/services/${service.serviceId}`, service);
    if (!response.success) return null;

    return response.data as Service;
  }

  static async setOfficeServiceEnabled(officeId: string, serviceId: string, enabled: boolean): Promise<Service | null> {
    const body = [
      {
        op: ApiPatchOp.Replace,
        path: "/enabled",
        value: enabled,
      },
    ];
    if (!enabled) {
      body.push({
        op: ApiPatchOp.Replace,
        path: "/featured",
        value: false,
      });
      body.push({
        op: ApiPatchOp.Replace,
        path: "/uninsured",
        value: false,
      });
    }
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/services/${serviceId}`, body);
    if (!response.success) return null;

    return response.data as Service;
  }

  static async duplicateServiceToOffice(officeId: string, serviceId: string): Promise<Service | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/services/${serviceId}/duplicate`, {});
    if (!response.success) return null;

    return response.data as Service;
  }

  static async duplicateAllServicesToOffice(officeId: string): Promise<Service[]> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/services/duplicate-all`, {});
    if (!response.success) return [];

    return response.data as Service[];
  }

  static async setOfficeAcceptedTerms(officeId: string, acceptedTerms: boolean): Promise<Office | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/acceptedTerms",
        value: acceptedTerms,
      },
    ]);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async setOfficePublished(officeId: string, published: OfficeState): Promise<Office | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/officeState",
        value: { name: published },
      },
    ]);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async getOfficeServices(officeId: string, page = 0, size = 20, sort = "name,asc"): Promise<ApiPage<Service>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/services?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<Service>();

    return response.data as ApiPage<Service>;
  }

  static async getOfficeService(officeId: string, serviceId: string): Promise<Service | null> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/services/${serviceId}`);
    if (!response.success) return null;

    return response.data as Service;
  }

  static async searchOfficeServices(
    officeId: string,
    term?: string,
    enabledOnly = false,
    serviceOwner?: string,
    page = 0,
    size = 20,
    sort = "name,asc",
    hasAnnualPlanDiscount?: boolean,
    annualPlanEnabled?: boolean,
    serviceBilledTo?: string
  ): Promise<ApiPage<Service>> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/services/search?page=${page}&size=${size}&sort=${sort}`, {
      term: term,
      enabledOnly: enabledOnly,
      serviceOwner: serviceOwner,
      hasAnnualPlanDiscount,
      annualPlanEnabled,
      featured: true,
      serviceBilledTo,
    });
    if (!response.success) return emptyApiPage<Service>();

    return response.data as ApiPage<Service>;
  }

  static async getOfficeUsers(officeId: string, page = 0, size = 20, sort = "lastName,asc"): Promise<ApiPage<User>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/users?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<User>();

    return response.data as ApiPage<User>;
  }

  static async searchOfficeUsers(
    officeId: string,
    term: string,
    page = 0,
    size = 20,
    sort = "lastName,asc",
    userState?: string,
    userType?: UserType
  ): Promise<ApiPage<User>> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/search?page=${page}&size=${size}&sort=${sort}`, {
      term: term ? term : undefined,
      userState,
      userType: userType ?? undefined,
    });
    if (!response.success) return emptyApiPage<User>();

    return response.data as ApiPage<User>;
  }

  static async getOfficeUsersWithPermission(
    officeId: string,
    permission: AuthPermission,
    page = 0,
    size = 100,
    sort = "lastName,asc"
  ): Promise<ApiPage<User>> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/search?page=${page}&size=${size}&sort=${sort}`, {
      permission: permission,
    });
    if (!response.success) return emptyApiPage<User>();

    return response.data as ApiPage<User>;
  }

  static async getOfficeUsersWithType(officeId: string, type: UserType, page = 0, size = 100, sort = "lastName,asc"): Promise<ApiPage<User>> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/search?page=${page}&size=${size}&sort=${sort}`, {
      userType: type,
    });
    if (!response.success) return emptyApiPage<User>();

    return response.data as ApiPage<User>;
  }

  static async getOfficeUser(officeId: string, userId: string): Promise<User | null> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/users/${userId}`);
    if (!response.success) return null;

    return response.data as User;
  }

  static async getOfficePatients(officeId: string, page = 0, size = 20, sort = "lastName,asc"): Promise<ApiPage<User>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/patients?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<User>();

    return response.data as ApiPage<User>;
  }

  static async searchOfficePatients(
    officeId: string,
    term?: string,
    page = 0,
    size = 20,
    sort = "lastName,asc",
    userState?: string,
    hasAnnualPlan?: boolean
  ): Promise<ApiPage<User>> {
    // When filtering for subscribed patients, use the dedicated subscribed search endpoint
    const isSubscribedSearch = hasAnnualPlan === true;
    const endpoint = isSubscribedSearch
      ? `${this.BASE_PATH}/${officeId}/patients/search/subscribed?page=${page}&size=${size}&sort=${sort}`
      : `${this.BASE_PATH}/${officeId}/patients/search?page=${page}&size=${size}&sort=${sort}`;

    const response = await apiService.post(endpoint, {
      term: term ? term : undefined,
      userState: userState ? userState : undefined,
      hasAnnualPlan,
    });
    if (!response.success) return emptyApiPage<User>();

    return response.data as ApiPage<User>;
  }

  static async getOfficeCareProviders(officeId: string): Promise<UserInfo[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/providers`);
    if (!response.success) return [];

    return response.data as UserInfo[];
  }

  static async getOfficeRequestCounts(officeId: string, assignedTo?: string, archiveState = ArchiveState.Any): Promise<RequestCounts | null> {
    let endpoint = `${this.BASE_PATH}/${officeId}/requests/status-counts?archiveState=${archiveState}`;
    if (assignedTo) {
      endpoint += `&assignedTo=${assignedTo}`;
    }
    const response = await apiService.get(endpoint);
    if (!response.success) return null;

    return response.data as RequestCounts;
  }

  static async getAllPCP(officeId: string): Promise<ApiPage<User>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/users/pcp`);
    if (!response.success) return emptyApiPage<User>();

    return response.data as ApiPage<User>;
  }

  static async getOfficeRequests(officeId: string, assignedTo?: string, page = 0, size = 20, sort = "createdDate,desc"): Promise<ApiPage<Request>> {
    let endpoint = `${this.BASE_PATH}/${officeId}/requests?page=${page}&size=${size}&sort=${sort}`;
    if (assignedTo) {
      endpoint += `&assignedTo=${assignedTo}`;
    }

    const response = await apiService.get(endpoint);
    if (!response.success) return emptyApiPage<Request>();

    return response.data as ApiPage<Request>;
  }

  static async getOfficeRequest(officeId: string, requestId: string): Promise<Request | null> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/requests/${requestId}`);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async searchOfficeRequests(
    officeId: string,
    term: string,
    assignedTo?: string,
    state?: RequestState,
    urgent?: boolean,
    archiveState = ArchiveState.Any,
    page = 0,
    size = 20,
    sort = "createdDate,desc",
    primaryCareProvider?: string
  ): Promise<ApiPage<Request>> {
    const body = {
      term: term,
      assignedTo: assignedTo,
      state: state,
      urgent: urgent,
      archiveState: archiveState,
      primaryCareProvider: primaryCareProvider,
    };
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/search?page=${page}&size=${size}&sort=${sort}`, body);
    if (!response.success) return emptyApiPage<Request>();

    return response.data as ApiPage<Request>;
  }

  static async getUrgentOfficeRequests(
    officeId: string,
    assignedTo?: string,
    page = 0,
    size = 20,
    sort = "createdDate,desc"
  ): Promise<ApiPage<Request>> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/search?page=${page}&size=${size}&sort=${sort}`, {
      urgent: true,
      assignedTo: assignedTo,
    });
    if (!response.success) return emptyApiPage<Request>();

    return response.data as ApiPage<Request>;
  }

  static async getOfficeRequestsByState(
    officeId: string,
    state: RequestState,
    assignedTo?: string,
    page = 0,
    size = 20,
    sort = "createdDate,desc"
  ): Promise<ApiPage<Request>> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/search?page=${page}&size=${size}&sort=${sort}`, {
      state: state,
      assignedTo: assignedTo,
    });
    if (!response.success) return emptyApiPage<Request>();

    return response.data as ApiPage<Request>;
  }

  static async updateOfficeRequestPatient(officeId: string, patientUpdate: RequestPatientUpdate): Promise<Request | null> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/requests/${patientUpdate.requestId}`, patientUpdate);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async setOfficeRequestUrgent(officeId: string, requestId: string, urgent: boolean): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/urgent",
        value: urgent,
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async setOfficeRequestState(officeId: string, requestId: string, state: RequestState, Reason?: any): Promise<Request | null> {
    const patchPayload: any[] = [
      {
        op: ApiPatchOp.Replace,
        path: "/requestState",
        value: {
          name: state,
        },
      },
    ];
    if (Reason) {
      patchPayload.push({ op: "replace", path: "/cancellationReason", value: "This request is cancelled by a patient." });
    }
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, patchPayload);
    if (!response.success) return null;

    return response.data as Request;
  }
  static async setOfficeRequestReason(officeId: string, requestId: string, reason: string, Reason?: any): Promise<Request | null> {
    const patchPayload: any[] = [
      {
        op: ApiPatchOp.Replace,
        path: "/cancellationReason",
        value: reason,
      },
    ];

    if (Reason) {
      patchPayload.push({
        op: ApiPatchOp.Replace,
        path: "/requestState",
        value: {
          name: "Cancelled",
        },
      });
    }

    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, patchPayload);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async setOfficeRequestPaid(
    officeId: string,
    requestId: string,
    paymentMethod: RequestPaymentMethod,
    paymentDate = new Date(),
    paymentDetails?: string | undefined,
    overridePrice?: number
  ): Promise<Request | null> {
    const body = [
      {
        op: ApiPatchOp.Replace,
        path: "/paymentComplete",
        value: overridePrice ? false : true,
      },
      {
        op: ApiPatchOp.Replace,
        path: "/paymentMethod",
        value: {
          name: paymentMethod,
        },
      },
      {
        op: ApiPatchOp.Replace,
        path: "/paymentDate",
        value: paymentDate.toISOString(),
      },
    ];
    if (paymentDetails?.trim()?.length) {
      body.push({
        op: ApiPatchOp.Replace,
        path: "/paymentDetails",
        value: paymentDetails,
      });
    }
    if (overridePrice && overridePrice > 0) {
      body.push(
        {
          op: ApiPatchOp.Replace,
          path: "/servicePriceOverride",
          value: overridePrice.toString(),
        },
        {
          op: ApiPatchOp.Replace,
          path: "/priceOverridden",
          value: true,
        }
      );
    }
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, body);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async setOfficeRequestSubmitted(officeId: string, requestId: string, status: boolean): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/submitted",
        value: status,
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async undoOfficePaymentStatus(officeId: string, requestId: string, status: boolean): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/paymentComplete",
        value: status,
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async getOfficeRequestFiles(officeId: string, requestId: string): Promise<RequestFile[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/requests/${requestId}/files`);
    if (!response.success) return [];

    return response.data as RequestFile[];
  }

  // The `fileIds` will be from `DsFile` model after initial upload(s)
  static async addOfficeRequestFiles(
    officeId: string,
    requestId: string,
    fileIds: string[],
    requestFileType?: RequestFileTypes
  ): Promise<Request | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/files`, {
      fileIds: fileIds,
      requestFileType: requestFileType ?? RequestFileTypes.Document,
    });
    if (!response.success) return null;

    return response.data as Request;
  }

  static async addOfficeRequestFinalDocuments(officeId: string, requestId: string, fileIds: string[]): Promise<Request | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/final-files`, {
      fileIds: fileIds,
      requestFileType: RequestFileTypes.Document,
    });
    if (!response.success) return null;

    return response.data as Request;
  }

  static async deleteOfficeRequestFile(officeId: string, requestId: string, requestFileId: string): Promise<Request | null> {
    const response = await apiService.delete(`${this.BASE_PATH}/${officeId}/requests/${requestId}/files/${requestFileId}`);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async getOfficeServiceSampleFile(officeId: string, serviceId: string): Promise<DsFile | null> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/services/${serviceId}/sample-file`);
    if (!response.success) return null;

    return response.data as DsFile;
  }

  // Requires *either* sender name or sender ID
  static async addOfficeRequestMessage(
    officeId: string,
    requestId: string,
    message: string,
    internal: boolean,
    senderId?: string,
    senderName?: string
  ): Promise<RequestMessage | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/messages`, {
      requestId: requestId,
      senderId: senderId,
      senderName: senderName,
      message: message,
      internal: internal,
    });
    if (!response.success) return null;

    return response.data as RequestMessage;
  }

  static async getOfficeRequestInternalMessages(officeId: string, requestId: string): Promise<RequestMessage[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/requests/${requestId}/messages/internal`);
    if (!response.success) return [];

    return response.data as RequestMessage[];
  }

  static async getOfficeRequestPatientMessages(officeId: string, requestId: string): Promise<RequestMessage[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/requests/${requestId}/messages/external`);
    if (!response.success) return [];

    return response.data as RequestMessage[];
  }

  static async setOfficeRequestMessageRead(officeId: string, requestId: string, messageId: string, read: boolean): Promise<RequestMessage | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}/messages/${messageId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/unread",
        value: !read,
      },
    ]);
    if (!response.success) return null;

    return response.data as RequestMessage;
  }

  static async updateOfficeRequestFees(officeId: string, requestId: string, fees: UpdateFee[]): Promise<Request | null> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/requests/${requestId}/fees`, fees);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async sendRequestFileToPatient(officeId: string, requestId: string, requestFileId: string): Promise<RequestFile | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/files/${requestFileId}/send-patient`, {});
    if (!response.success) return null;

    return response.data as RequestFile;
  }

  static async generatePatientLetter(officeId: string, requestId: string, message: string): Promise<RequestFile | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/patient-note`, {
      message: message,
    });
    if (!response.success) return response.data;

    return response.data as RequestFile;
  }

  static async sendRequestFileToThirdParty(officeId: string, requestId: string, requestFileId: string): Promise<RequestFile | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/files/${requestFileId}/send-recipient`, {});
    if (!response.success) return null;

    return response.data as RequestFile;
  }

  static async getOfficeRequestPriceDetails(officeId: string, requestId: string): Promise<PriceDetails | null> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/requests/${requestId}/price-details`);
    if (!response.success) return null;

    return response.data as PriceDetails;
  }

  static async createOfficeRequestInvoice(officeId: string, requestId: string, notes: string): Promise<PriceDetails | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/invoice`, {
      notes: notes,
    });
    if (!response.success) return null;

    return response.data as PriceDetails;
  }

  static async inviteOfficeUser(
    firstName: string,
    lastName: string,
    email: string,
    jobTitleId: string,
    userType: UserType = UserType.SystemAdmin,
    permissions: AuthPermission[],
    primaryCareProvider: boolean,
    officeId: string,
    phone?: string
  ): Promise<InvitationResponse | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/invite`, {
      firstName: firstName,
      lastName: lastName,
      email: email,
      userType: userType,
      permissions: permissions,
      primaryCareProvider: primaryCareProvider,
      officeId: officeId,
      phone: phone,
      jobTitleId,
    });

    if (!response.success) {
      throw new ApiError(response.status === 409 ? ApiErrorCode.EXISTS : ApiErrorCode.UNKNOWN, JSON.stringify(response.data));
    }

    return response.data as InvitationResponse;
  }

  static async cancelOfficeUserInvitation(officeId: string, userId: string): Promise<boolean> {
    const response = await apiService.delete(`${this.BASE_PATH}/${officeId}/users/invite/${userId}`);
    if (!response.success) return false;

    return true;
  }

  static async cancelOfficePatientInvitation(officeId: string, userId: string): Promise<any> {
    const response = await apiService.delete(`${this.BASE_PATH}/${officeId}/patients/invite/${userId}`);
    if (!response.success) return response?.data as any;

    return response.data;
  }

  static async resendOfficeUserInvitation(officeId: string, userId: string): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/invite-resend`, {
      userId: userId,
    });
    if (!response.success) return false;

    return true;
  }

  static async resendOfficePatientInvitation(officeId: string, userId: string): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/invite-patient-resend`, {
      userId: userId,
    });
    if (!response.success) return false;

    return true;
  }

  static async updateOfficeUser(officeId: string, user: UserUpdate): Promise<User | null> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/users/${user.userId}`, user);
    if (!response.success) return response.data as User;

    return response.data as User;
  }

  static async getOfficeRequestTimeline(officeId: string, requestId: string, page = 0, size = 20): Promise<ApiPage<Timeline>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/requests/${requestId}/timeline?page=${page}&size=${size}`);
    if (!response.success) return emptyApiPage<Timeline>();

    return response.data as ApiPage<Timeline>;
  }

  static async addOfficeRequestTimelineEvent(event: string, officeId: string, requestId: string): Promise<Timeline | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/timeline`, {
      event: event,
    });
    if (!response.success) return null;

    return response.data as Timeline;
  }

  static async getOfficeFinanceReport(officeId: string, startDate: Date, endDate?: Date, mtd?: string): Promise<Finance | null> {
    let adjustedEndDate = endDate ?? null;
    // if (mtd && adjustedEndDate) {
    //   adjustedEndDate = new Date(
    //     Date.UTC(adjustedEndDate.getUTCFullYear(), adjustedEndDate.getUTCMonth(), adjustedEndDate.getUTCDate(), 23, 59, 59, 999)
    //   );
    // }
    if (mtd) {
      adjustedEndDate = new Date();
    }
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/finance/get-finance-summary`, {
      startDate: startDate.toISOString(),
      endDate: adjustedEndDate?.toISOString(),
    });
    if (!response.success) return null;

    return response.data as Finance;
  }

  static async getOfficeFinanceReportCsv(officeId: string, startDate: Date, endDate?: Date): Promise<string | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/finance/requests/csv`, {
      startDate: startDate.toISOString(),
      endDate: endDate?.toISOString(),
    });
    if (!response.success) return null;
    return response.data as string;
  }

  static async getOfficeAnnualPlanReportCsv(officeId: string, startDate: Date, endDate?: Date): Promise<string | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/annual-plans/csv`, {
      startDate: startDate.toISOString(),
      endDate: endDate?.toISOString(),
    });
    if (!response.success) return null;
    return response.data as string;
  }

  static async getOfficeReportForMTD(officeId: string, monthYearKey?: string): Promise<string | null> {
    const { success, data } = await apiService.get(`${this.BASE_PATH}/${officeId}/download/csv?fileName=${monthYearKey}`);
    return success ? (data as string) : null;
  }

  static async getOfficeFinanceRequests(
    officeId: string,
    startDate: Date,
    endDate: Date,
    page = 0,
    size = 10,
    sort = "request.lastName"
  ): Promise<ApiPage<FinanceRequest>> {
    const body = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/finance/requests?page=${page}&size=${size}&sort=${sort}`, body);
    if (!response.success) return emptyApiPage<FinanceRequest>();

    return response.data as ApiPage<FinanceRequest>;
  }

  static async sendOfficeRequestTextMessage(officeId: string, requestId: string, message: string): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/send-sms`, {
      message: message,
    });
    if (!response.success) return false;

    return true;
  }

  static async declineOfficeRequest(officeId: string, requestId: string, reason: string): Promise<Request | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/decline`, {
      reason: reason,
    });
    if (!response.success) return null;

    return response.data as Request;
  }

  static async submitSupportForm(officeId: string, message: string, file?: File): Promise<boolean> {
    const formData = new FormData();
    formData.append("message", message);
    if (file) {
      formData.append("file", file);
    }
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/support`, formData);
    if (!response.success) return false;

    return true;
  }

  static async invitePatient(officeId: string, email: string, note: string): Promise<InvitationResponse | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/invite-patient`, {
      officeId,
      email,
      note,
    });
    if (!response.success) {
      throw new ApiError(response.status === 409 ? ApiErrorCode.EXISTS : ApiErrorCode.UNKNOWN, JSON.stringify(response.data));
    }

    return response.data as InvitationResponse;
  }

  static async acceptPatientInvitation(
    officeId: string,
    token: string,
    email: string,
    phone: string,
    password: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    acceptedTerms: boolean,
    ohipNumber?: string
  ): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/invite-patient-accept`, {
      token,
      email,
      phone,
      password,
      acceptedTerms,
      firstName,
      lastName,
      dateOfBirth,
      ohipNumber,
    });
    if (!response.success) return false;

    return true;
  }

  static async sendPaymentReminder(officeId: string, requestId: string): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/send-payment-reminder`, {});
    if (!response.success) return false;

    return true;
  }

  static async sendInviteReminder(officeId: string, requestId: string): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/send-invite-reminder`, {});
    if (!response.success) return false;

    return true;
  }

  static async getPriceReview(officeId: string, requestBody: UpdateRequest): Promise<PriceDetails | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/price-preview`, requestBody);
    if (!response.success) return null;

    return response.data;
  }

  static async updateRequest(request: UpdateRequest, requestId: string, officeId: string): Promise<Request | null> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, request);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async updateRequestNotes(details: string, requestId: string, officeId: string): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/details",
        value: details,
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async updateRequestRecipients(name: string, contact: string, requestId: string, officeId: string): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/recipientName",
        value: name,
      },
      {
        op: ApiPatchOp.Replace,
        path: "/recipientContact",
        value: contact,
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async updateRequestAssignee(
    assignedToUserId: string,
    primaryCareProviderUserId: string,
    requestId: string,
    officeId: string
  ): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/assignedTo",
        value: {
          userId: assignedToUserId,
        },
      },
      {
        op: ApiPatchOp.Replace,
        path: "/primaryCareProvider",
        value: {
          userId: primaryCareProviderUserId,
        },
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async updateOfficeRequestFulfillOnPayment(officeId: string, requestId: string, fulfill: boolean): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/fulfillOnPayment",
        value: fulfill,
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async getOfficeAccountReceivablesTotals(officeId: string): Promise<AccountReceivablesTotals | undefined> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/account-receivables-totals`);
    if (!response.success) return undefined;

    return response.data as AccountReceivablesTotals;
  }

  static async getOfficeAccountReceivablesPayee(officeId: string): Promise<Payee[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/account-receivables-payee`);
    if (!response.success) return [];

    return response.data as Payee[];
  }

  static async getOfficeAccountReceivablesTopOverdueAccounts(officeId: string): Promise<TopOverdueAccounts[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/top-overdue-accounts`);
    if (!response.success) return [];

    return response.data as TopOverdueAccounts[];
  }

  static async getOfficeAccountReceivables(officeId: string, page = 0, size = 10, sort = "requestNumber,desc"): Promise<ApiPage<TopOverdueAccounts>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/account-receivables?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<TopOverdueAccounts>();

    return response.data as ApiPage<TopOverdueAccounts>;
  }

  static async setOfficeTemporarilyClosed(officeId: string, closed: boolean, reason?: string): Promise<Office | null> {
    const body = [];

    if (closed && reason?.length) {
      body.push({
        op: ApiPatchOp.Replace,
        path: "/tempClosedReason",
        value: reason.toString(),
      });
    }

    body.push({
      op: ApiPatchOp.Replace,
      path: "/tempClosed",
      value: closed,
    });

    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}`, body);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async refundRequest(officeId: string, requestId: string, requestReason: string, requestReasonOther?: string): Promise<Request | null> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/requests/${requestId}/refund-request`, {
      requestReason,
      requestReasonOther,
    });
    if (!response.success) return null;

    return response.data;
  }

  static async approveRefundRequest(officeId: string, requestId: string, refundRequestId: string): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}/refund-request/${refundRequestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/refundRequestState",
        value: {
          name: "approved",
        },
      },
    ]);
    if (!response.success) return null;

    return response.data;
  }

  static async rejectRefundRequest(officeId: string, requestId: string, refundRequestId: string, reason: string): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}/refund-request/${refundRequestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/refundRequestState",
        value: {
          name: "rejected",
        },
      },
      {
        op: ApiPatchOp.Replace,
        path: "/rejectedReason",
        value: reason,
      },
    ]);
    if (!response.success) return null;

    return response.data;
  }

  static async getOfficeUninsuredServices(officeId: string, page = 0, size = 200, sort = "name,asc"): Promise<ApiPage<Service>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/services/uninsured?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<Service>();

    return response.data as ApiPage<Service>;
  }
  static async getOfficeArchivedServices(officeId: string, page = 0, size = 200, sort = "name,asc"): Promise<ApiPage<Service>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/services/archived?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<Service>();

    return response.data as ApiPage<Service>;
  }

  static async restoreOfficeService(officeId: string, serviceId: string): Promise<Service | null> {
    const body = [
      {
        op: ApiPatchOp.Replace,
        path: "/archived",
        value: false,
      },
    ];
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/services/${serviceId}`, body);
    if (!response.success) return null;

    return response.data as Service;
  }

  static async archiveOfficeService(officeId: string, serviceId: string): Promise<Service | null> {
    const body = [
      {
        op: ApiPatchOp.Replace,
        path: "/archived",
        value: true,
      },
      {
        op: ApiPatchOp.Replace,
        path: "/enabled",
        value: false,
      },
    ];
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/services/${serviceId}`, body);
    if (!response.success) return null;

    return response.data as Service;
  }

  static async getPatientReceipts(officeId: string, userId: string, page = 0, size = 10, sort = "name,asc"): Promise<ApiPage<Receipt>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/patients/${userId}/receipts?page=${page}&size=${size}`);
    if (!response.success) return emptyApiPage<Receipt>();

    return response.data as ApiPage<Receipt>;
  }

  static async getPatientPlanReceipts(officeId: string, userId: string, page = 0, size = 10, sort = "name,asc"): Promise<ApiPage<Receipt>> {
    const response = await apiService.get(`/api/v1/users/${userId}/annual-plan/files`);
    if (!response.success) return emptyApiPage<Receipt>();

    return response.data as ApiPage<any>;
  }

  static async getPatientPlanInvoice(
    officeId: string,
    userId: string,
    userAnnualPlanId: string,
    page = 0,
    size = 10,
    sort = "name,asc"
  ): Promise<ApiPage<Receipt>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/users/${userId}/annual-plans/${userAnnualPlanId}/invoice/all`);
    if (!response.success) return emptyApiPage<Receipt>();

    return response.data as ApiPage<any>;
  }

  static async getPatientDocuments(officeId: string, userId: string, page = 0, size = 10, sort = "createdDate,desc"): Promise<ApiPage<Document>> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/patients/${userId}/documents?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<Document>();

    return response.data as ApiPage<Document>;
  }

  static async setOfficeRefundReason(officeId: string, requestId: string, reason: string, paymentDate = new Date()): Promise<Request | null> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/requests/${requestId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/refundReason",
        value: reason,
      },
      {
        op: ApiPatchOp.Replace,
        path: "/refunded",
        value: true,
      },
      {
        op: ApiPatchOp.Replace,
        path: "/refundDate",
        value: paymentDate.toISOString(),
      },
    ]);
    if (!response.success) return null;

    return response.data as Request;
  }

  static async officeSignUp(body: OfficeSignUp): Promise<Office | undefined> {
    const response = await apiService.post(`${this.BASE_PATH}/signup`, body);
    if (!response.success) return undefined;

    return response.data as Office;
  }

  static async officeSignUpV2(body: OfficeSignUpV2): Promise<Office | undefined> {
    const response = await apiService.post(`${this.BASE_PATH_V2}/signup`, body);
    if (!response.success) return undefined;

    return response.data as Office;
  }

  static async updateOfficeV2(
    officeId: string,
    data: {
      name: string;
      street: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      country: string;
      phone: string;
      fax: string;
      province?: string;
    }
  ): Promise<Office | undefined> {
    const response = await apiService.put(`${this.BASE_PATH_V2}/${officeId}`, {
      officeId,
      ...data,
    });
    if (!response.success) return undefined;

    return response.data as Office;
  }

  static async getOfficeV2(officeId: string): Promise<Office | null> {
    const response = await apiService.get(`${this.BASE_PATH_V2}/${officeId}`);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async updateLetterHeader(officeId: string, letterHeaderText: any): Promise<boolean> {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}${this.BASE_PATH_V2}/${officeId}/letterHeader`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify(letterHeaderText),
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  static async updateOfficeState(officeId: string, state: OfficeState, reason?: string): Promise<Office | null> {
    const body = [];

    body.push({
      op: ApiPatchOp.Replace,
      path: "/officeState",
      value: {
        name: state,
      },
    });

    if (state === OfficeState.Rejected && reason?.length) {
      body.push({
        op: ApiPatchOp.Replace,
        path: "/rejectedReason",
        value: reason.toString(),
      });
    }

    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}`, body);
    if (!response.success) return null;

    return response.data as Office;
  }

  static async inviteBulkOfficePatient(officeId: string, body: PatientInviteObject[]): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/invite-patient-bulk`, body);
    if (!response.success) return false;

    return true;
  }

  static async downloadAnnualSubscribersCSV(
    officeId: string,
    sort = "lastName,asc",
    userState?: string,
    hasAnnualPlan?: boolean,
    selectedFilter?: string
  ): Promise<Blob | undefined> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/patients/search/downloadCsv?sort=${sort}`, {
      userState: userState ? userState : undefined,
      hasAnnualPlan,
      patientExportType: selectedFilter?.toUpperCase(),
    });
    if (!response.success) return undefined;

    return new Blob([response.data], { type: "text/csv" });
  }

  static async getProvinces(): Promise<{ provinceId: string; name: string; provinceShortName: string; pst: number }[]> {
    const response = await apiService.get(`${this.BASE_PATH_V2}/provinces`);
    if (!response.success) return [];

    return response.data as { provinceId: string; name: string; provinceShortName: string; pst: number }[];
  }
}
